import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getGuestCart } from '@/lib/guestCart';
import { useCurrentUserSafe } from '@/contexts/UserContext';

/**
 * useCartCount - Shared hook for cart count across all header components
 * 
 * Now uses the centralized UserContext to avoid redundant auth calls.
 * Returns the total quantity of items in the cart (sum of all item quantities).
 * Works for both logged-in users (database) and guests (localStorage).
 */
export function useCartCount() {
  const userContext = useCurrentUserSafe();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadCartCount = useCallback(async () => {
    try {
      setLoading(true);
      
      // Use user from context instead of calling getUser()
      const user = userContext?.user;
      
      if (user) {
        // Logged-in user: fetch from database
        const { data: cart } = await supabase
          .from('carts')
          .select('id')
          .eq('profile_id', user.id)
          .maybeSingle();

        if (cart) {
          const { data: items } = await supabase
            .from('cart_items')
            .select('qty')
            .eq('cart_id', cart.id);

          const total = items?.reduce((sum, item) => sum + item.qty, 0) || 0;
          setCount(total);
        } else {
          setCount(0);
        }
      } else {
        // Guest: count from localStorage
        const guestItems = getGuestCart();
        const total = guestItems.reduce((sum, item) => sum + item.qty, 0);
        setCount(total);
      }
    } catch (error) {
      console.error('[useCartCount] Error loading cart count:', error);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [userContext?.user]);

  useEffect(() => {
    // Wait for user context to be ready
    if (userContext?.status === 'loading') return;
    
    loadCartCount();
  }, [userContext?.status, userContext?.user?.id, loadCartCount]);

  // Set up realtime subscription for cart_items changes (logged-in users only)
  useEffect(() => {
    const user = userContext?.user;
    if (!user) return; // Don't subscribe for guests
    
    // Debounce to prevent rapid-fire updates
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    
    const cartItemsSubscription = supabase
      .channel(`cart_items_changes_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cart_items',
        },
        () => {
          // Debounce to prevent multiple rapid refetches
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            loadCartCount();
          }, 200);
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      cartItemsSubscription.unsubscribe();
    };
  }, [userContext?.user?.id, loadCartCount]);

  // Listen for guest cart changes via custom event
  useEffect(() => {
    const handleCartChanged = () => {
      loadCartCount();
    };

    window.addEventListener('cart:changed', handleCartChanged);
    return () => {
      window.removeEventListener('cart:changed', handleCartChanged);
    };
  }, [loadCartCount]);

  return {
    count,
    loading,
    refresh: loadCartCount,
  };
}
